import React, { useContext } from 'react';
import moment from 'moment';
import { styled } from '@mui/material';

import { PageContext } from 'pages/PeerEvaluation/PageProvider';
import {
    Button,
    Icon,
    Box,
    Stack,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CompletionButton,
    Text,
    Divider,
    Tag,
    Tooltip,
} from 'shell/components';
import { useViewport } from 'contexts/viewport';
import { getTooltipComponent } from 'utils/helper';

const getLateTag = (peerEvaluation) => {
    const today = new Date().getTime();
    if (
        peerEvaluation.due_on < peerEvaluation?.student_submission?.created_on ||
        (!peerEvaluation?.student_submission &&
            peerEvaluation.due_on < today &&
            peerEvaluation.start_on < today &&
            peerEvaluation.end_on > today)
    ) {
        return (
            <Tag
                type={'submission'}
                tooltip={{
                    component: getTooltipComponent(
                        'Late Submission',
                        'The due date has passed but the evaluation is still open for late submissions'
                    ),
                }}
                value={'late'}
            />
        );
    }
    return;
};

const getStatusTag = (peerEvaluation) => {
    let kind = 'fileUploaded';
    let text = 'Published';
    let tooltip = { title: 'Status: Open', description: 'Actively accepting submissions' };
    const today = moment().valueOf();
    if (peerEvaluation.start_on > today || peerEvaluation.is_released) {
        kind = 'lock';
        text = 'Locked';
        tooltip = { title: 'Status: Locked', description: 'Availability window has not opened yet' };
    } else if (peerEvaluation.start_on <= today && peerEvaluation.end_on >= today) {
        kind = 'unlock';
        text = 'Open';
        tooltip = { title: 'Status: Open', description: 'Actively accepting submissions' };
    } else {
        kind = 'exit';
        text = 'Closed';
        tooltip = { title: 'Status: Closed', description: 'Submissions are no longer being accepted' };
    }

    return (
        <Stack alignItems={'center'} direction={'row'} spacing={2}>
            <Icon kind={kind} style={{ fontSize: 12 }} />
            <Text
                tooltip={{
                    component: getTooltipComponent(tooltip.title, tooltip.description),
                    maxWidth: '140px',
                }}
            >
                {text}
            </Text>
        </Stack>
    );
};

const getCompeletionButton = (peerEvaluation) => {
    const hasSubmission = peerEvaluation.student_submission ? true : false;
    return (
        <Stack alignItems={'center'} direction={'row'} spacing={1}>
            <CompletionButton
                aria-label={hasSubmission ? 'completed' : 'not completed'}
                checked={hasSubmission}
                tooltip={{ component: hasSubmission ? 'Submitted' : 'Not submitted' }}
            />
        </Stack>
    );
};

const getCompletionComponent = (device, peerEvaluation, startPeerEvaluation) => {
    const today = moment().valueOf();
    const isReleased =
        peerEvaluation.is_released || (peerEvaluation.release_to_student && peerEvaluation.released_on <= today);

    if (isReleased) {
        return (
            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                {device === 'mobile' && getCompeletionButton(peerEvaluation)}
                <Button variant={'tertiary'} onClick={() => startPeerEvaluation(peerEvaluation.id)}>
                    View Result
                </Button>
            </Stack>
        );
    } else if (!peerEvaluation.release_to_student && peerEvaluation.end_on < today) {
        return (
            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                {device === 'mobile' && getCompeletionButton(peerEvaluation)}

                <Button
                    disabled={true}
                    tooltip={{
                        component:
                            'Results for this evaluation are not available as the instructor has opted to not release them.',
                    }}
                    variant={'tertiary'}
                >
                    View Result
                </Button>
            </Stack>
        );
    }

    if (
        // When there is no submission and released the grade
        (peerEvaluation.start_on > today || peerEvaluation.end_on < today) &&
        !isReleased &&
        !peerEvaluation.student_submission
    ) {
        return (
            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                {device === 'mobile' && getCompeletionButton(peerEvaluation)}
                <Button disabled={true} variant={'tertiary'}>
                    View Evaluation
                </Button>
            </Stack>
        );
    } else if (
        // When there is no submission and not released the grade
        peerEvaluation.start_on <= today &&
        peerEvaluation.end_on >= today &&
        !peerEvaluation.student_submission &&
        !peerEvaluation.is_released
    ) {
        return (
            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                {device === 'mobile' && getCompeletionButton(peerEvaluation)}
                <Button
                    data-cy={`start-evaluation-${peerEvaluation.title}`}
                    variant={'tertiary'}
                    onClick={() => startPeerEvaluation(peerEvaluation.id)}
                >
                    Start Evaluation
                </Button>
            </Stack>
        );
    } else {
        return (
            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                {device === 'mobile' && getCompeletionButton(peerEvaluation)}
                <Button
                    data-cy={`view-evaluation-${peerEvaluation.title}`}
                    variant={'tertiary'}
                    onClick={() => startPeerEvaluation(peerEvaluation.id)}
                >
                    View Evaluation
                </Button>
            </Stack>
        );
    }
};

const PeerEvaluationTableStudent = ({ peerEvaluationList = [], isInCourseDashboard }) => {
    const { device } = useViewport();
    const { startPeerEvaluation } = useContext(PageContext);

    const renderDesktop = () => {
        return (
            <TableOuterBox device={device} isInCourseDashboard={isInCourseDashboard}>
                <TableInnerBox device={device} isInCourseDashboard={isInCourseDashboard}>
                    <CustomTable device={device} size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Available from</TableCell>
                                <TableCell>Completion</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(peerEvaluationList || [])
                                .filter((peerEvaluation) => peerEvaluation.status === 'published')
                                .map((peerEvaluation) => (
                                    <TableRow key={peerEvaluation.id}>
                                        <TableCell noWrap>
                                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                                <>{peerEvaluation.title}</>
                                                {getLateTag(peerEvaluation)}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{getStatusTag(peerEvaluation)}</TableCell>
                                        <TableCell>
                                            {moment(peerEvaluation.due_on).format('MMM D [at] hh:mm a')}
                                        </TableCell>
                                        <TableCell>
                                            {moment(peerEvaluation.start_on).format('MMM D [at] hh:mm a')} -{' '}
                                            {moment(peerEvaluation.end_on).format('MMM D [at] hh:mm a')}
                                        </TableCell>
                                        <TableCell>
                                            {getCompletionComponent('mobile', peerEvaluation, startPeerEvaluation)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </CustomTable>
                </TableInnerBox>
            </TableOuterBox>
        );
    };

    const renderTablet = () => {
        return (
            <Stack spacing={2}>
                <Stack>
                    <Text variant={'p2'} color={'light'}>
                        Name
                    </Text>
                </Stack>
                <Divider />
                <Stack divider={<Divider />} spacing={4}>
                    {(peerEvaluationList || [])
                        .filter((peerEvaluation) => peerEvaluation.status === 'published')
                        .map((peerEvaluation) => (
                            <TabletPeerEvaluationStack key={peerEvaluation.id} spacing={1}>
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <Text variant={'p1'}>{peerEvaluation.title}</Text>
                                        {getLateTag(peerEvaluation)}
                                    </Stack>

                                    {getCompeletionButton(peerEvaluation)}
                                </Stack>

                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    {getStatusTag(peerEvaluation)}
                                </Stack>

                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text variant={'p2'} color={'light'}>
                                        {`Due: ${moment(peerEvaluation.due_on).format('MMM D')} / Available: ${moment(
                                            peerEvaluation.start_on
                                        ).format('MMM D')} - ${moment(peerEvaluation.end_on).format('MMM D')}`}
                                    </Text>
                                    {getCompletionComponent('tablet', peerEvaluation, startPeerEvaluation)}
                                </Stack>
                            </TabletPeerEvaluationStack>
                        ))}
                </Stack>
                <Divider />
            </Stack>
        );
    };

    const renderMobile = () => {
        return (
            <Stack spacing={4}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p2'} color={'light'}>
                        Name
                    </Text>
                </Stack>
                <Divider />
                <Stack divider={<Divider />}>
                    {(peerEvaluationList || [])
                        .filter((peerEvaluation) => peerEvaluation.status === 'published')
                        .map((peerEvaluation) => (
                            <TabletPeerEvaluationStack key={peerEvaluation.id} spacing={1}>
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <Text variant={'p1'}>{peerEvaluation.title}</Text>
                                        {getLateTag(peerEvaluation)}
                                    </Stack>
                                    {getCompeletionButton(peerEvaluation)}
                                </Stack>

                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    {getStatusTag(peerEvaluation)}
                                </Stack>

                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text variant={'p2'} color={'light'}>
                                        {`Due: ${moment(peerEvaluation.due_on).format('MMM D')} / Available: ${moment(
                                            peerEvaluation.start_on
                                        ).format('MMM D')} - ${moment(peerEvaluation.end_on).format('MMM D')}`}
                                    </Text>
                                </Stack>

                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    {getCompletionComponent('desktop', peerEvaluation, startPeerEvaluation)}
                                </Stack>
                            </TabletPeerEvaluationStack>
                        ))}
                </Stack>
                <Divider />
            </Stack>
        );
    };

    const responsiveTable = {
        mobile: renderMobile(),
        tablet: renderTablet(),
        desktop: renderDesktop(),
    };

    return responsiveTable[device];
};

const TableOuterBox = styled(Box)(({ device, isInCourseDashboard, theme }) => {
    return {
        height: '100%',
        backgroundColor: theme.palette.ensightful.shades[0],
        borderRadius: '12px',
        ...(device === 'mobile' && {
            margin: theme.spacing(0, -5),
            width: '100vw',
            overflowX: 'auto',
        }),
        ...(device === 'tablet' && {
            margin: theme.spacing(0, -5),
            overflowX: 'auto',
            ...(isInCourseDashboard && {
                margin: theme.spacing(0, -4),
                width: 'calc(100vw - 229px - 40px - 1px)',
            }),
        }),
    };
});

const TableInnerBox = styled(Box)(({ device, isInCourseDashboard, theme }) => {
    return {
        ...(device === 'mobile' && {
            width: 'fit-content',
            padding: theme.spacing(0, 5),
        }),
        ...(device === 'tablet' && {
            width: 'fit-content',
            padding: theme.spacing(0, 5),
            ...(isInCourseDashboard && {
                padding: theme.spacing(0, 4),
            }),
        }),
    };
});

const CustomTable = styled(Table)(({ device, theme }) => {
    return {
        ...(device !== 'mobile' && {
            tableLayout: 'fixed',
            marginTop: theme.spacing(2),
            '& > thead > tr > th:nth-of-type(1)': {
                width: '20%',
            },
            '& > thead > tr > th:nth-of-type(2)': {
                width: '10%',
            },
            '& > thead > tr > th:nth-of-type(3)': {
                width: '15%',
            },
            '& > thead > tr > th:nth-of-type(4)': {
                width: '25%',
            },
            '& > thead > tr > th:nth-of-type(5)': {
                width: '20%',
            },
            '& > thead > tr > th:nth-of-type(6)': {
                width: '56px',
            },
            '& > tbody > tr > td:nth-of-type(1)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(2)': {
                width: '15%',
            },
            '& > tbody > tr > td:nth-of-type(3)': {
                width: '15%',
            },
            '& > tbody > tr > td:nth-of-type(4)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(5)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(6)': {
                width: '56px',
            },
        }),
        ...(device === 'tablet' && {
            minWidth: '50rem',
        }),
    };
});

const TabletPeerEvaluationStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 4),
    };
});

const CustomViewSubmissionButton = styled(Button)(() => {
    return {
        padding: 0,
        border: 'none',
    };
});

export default PeerEvaluationTableStudent;
